import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from "recharts";
import { fmtNum, fmtPct, fmtCurrency, fmtDuration } from "./format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Kpi = ({ label, value, sub, testid }) => (
  <div className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</div>
    <div className="text-sm font-mono text-white mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-[#71717A] mt-0.5">{sub}</div>}
  </div>
);

const Pill = ({ children, color = "#A1A1AA", bg = "#52525B22" }) => (
  <span className="text-[10px] px-2 py-0.5 rounded font-medium" style={{ color, background: bg }}>{children}</span>
);

const priorityColor = (p) => {
  if (p === "P0") return { bg: "#FF444422", color: "#FF8080" };
  if (p === "P1") return { bg: "#F59E0B22", color: "#FCD34D" };
  return { bg: "#10B98122", color: "#6EE7B7" };
};

/**
 * Drill-down panel that opens inside the Funnel widget when a user clicks a
 * traffic source tab or a step bar. Renders KPIs + trend chart (for source
 * drill-down) + AI exec summary + recommendations.
 *
 * Props:
 *  - clientId
 *  - dimension: "source" | "step"
 *  - value: e.g. "Mobile", "Paid Social" or "add_to_cart"
 *  - startDate, endDate
 *  - currency
 *  - onClose: () => void
 */
export default function FunnelDrillDown({ clientId, dimension, value, startDate, endDate, currency = "INR", onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resyncing, setResyncing] = useState(false);

  const load = async (force = false) => {
    setLoading(!force);
    setResyncing(force);
    setError(null);
    try {
      const res = await axios.get(`${API}/intelligence/${clientId}/behaviour/funnel-drilldown`, {
        params: { dimension, value, start_date: startDate, end_date: endDate, force_refresh: force },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load drill-down");
    } finally {
      setLoading(false);
      setResyncing(false);
    }
  };

  useEffect(() => { load(false); }, [clientId, dimension, value, startDate, endDate]);  // eslint-disable-line react-hooks/exhaustive-deps

  const trendForChart = useMemo(() => {
    if (!data?.trend) return [];
    return data.trend.map((d) => ({
      date: d.date?.slice(5) || d.date,
      sessions: d.sessions,
      revenue: d.revenue,
    }));
  }, [data]);

  return (
    <div
      className="mt-3 rounded border border-[#FFEB0044] bg-[#0F0F0F]"
      data-testid="funnel-drilldown-panel"
      data-dimension={dimension}
      data-value={value}
    >
      <div className="flex items-start justify-between gap-2 p-3 border-b border-[#2A2A2A]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#FFEB00]">
            {dimension === "source" ? "Traffic source drill-down" : "Funnel step drill-down"}
          </div>
          <h4 className="text-sm font-semibold text-white">{value}</h4>
          <p className="text-[11px] text-[#71717A]">
            {data?.range?.label || `${startDate} → ${endDate}`}
            {data?.cached && <span className="ml-2 text-[#A1A1AA]">· cached</span>}
            {data?.updated_at && <span className="ml-2 text-[#52525B]">· {new Date(data.updated_at).toLocaleString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)}
            disabled={resyncing}
            className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white disabled:opacity-50"
            data-testid="funnel-drilldown-resync"
          >
            {resyncing ? "Resyncing…" : "Resync"}
          </button>
          <button
            onClick={onClose}
            className="text-[12px] text-[#71717A] hover:text-white px-1"
            data-testid="funnel-drilldown-close"
            aria-label="Close drill-down"
          >
            ✕
          </button>
        </div>
      </div>

      {loading && <div className="p-4 text-[11px] text-[#71717A]" data-testid="funnel-drilldown-loading">Loading drill-down…</div>}
      {error && <div className="p-4 text-[11px] text-red-400" data-testid="funnel-drilldown-error">{error}</div>}

      {!loading && !error && data && (
        <div className="p-3 space-y-4">
          {/* KPI STRIP */}
          {dimension === "source" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="funnel-drilldown-kpis-source">
              <Kpi label="Sessions" value={fmtNum(data.kpis.sessions, { compact: true, decimals: 0 })} testid="drill-kpi-sessions" />
              <Kpi label="Users" value={fmtNum(data.kpis.users, { compact: true, decimals: 0 })} testid="drill-kpi-users" />
              <Kpi label="Transactions" value={fmtNum(data.kpis.transactions, { compact: true, decimals: 0 })} testid="drill-kpi-transactions" />
              <Kpi label="Revenue" value={fmtCurrency(data.kpis.revenue, currency)} testid="drill-kpi-revenue" />
              <Kpi label="Conversion" value={fmtPct(data.kpis.conversion_rate)} testid="drill-kpi-cr" />
              <Kpi label="AOV" value={fmtCurrency(data.kpis.aov, currency)} testid="drill-kpi-aov" />
              <Kpi label="Bounce" value={fmtPct(data.kpis.bounce_rate)} testid="drill-kpi-bounce" />
              <Kpi label="Engagement" value={fmtPct(data.kpis.engagement_rate)} testid="drill-kpi-engagement" />
              <Kpi label="Avg Session" value={fmtDuration(data.kpis.avg_session_seconds)} testid="drill-kpi-avgsession" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="funnel-drilldown-kpis-step">
              <Kpi label="Users" value={fmtNum(data.kpis.users, { compact: true, decimals: 0 })} testid="drill-kpi-step-users" />
              <Kpi label="Events" value={fmtNum(data.kpis.event_count, { compact: true, decimals: 0 })} testid="drill-kpi-step-events" />
              <Kpi
                label="Drop from previous"
                value={`-${data.kpis.drop_off_from_previous_pct ?? 0}%`}
                sub={data.kpis.previous_step ? `from ${data.kpis.previous_step}` : null}
                testid="drill-kpi-step-dropfrom"
              />
              {data.kpis.drop_off_to_next_pct != null && (
                <Kpi
                  label="Drop to next"
                  value={`-${data.kpis.drop_off_to_next_pct}%`}
                  sub={data.kpis.next_step ? `to ${data.kpis.next_step}` : null}
                  testid="drill-kpi-step-dropto"
                />
              )}
              <Kpi label="Previous step users" value={fmtNum(data.kpis.previous_step_users, { compact: true, decimals: 0 })} testid="drill-kpi-step-prevusers" />
            </div>
          )}

          {/* TREND CHART (source only — daily series) */}
          {dimension === "source" && trendForChart.length > 0 && (
            <div className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="funnel-drilldown-trend-chart">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div>
                  <div className="text-[11px] font-semibold text-white">Sessions & Revenue · daily</div>
                  <div className="text-[10px] text-[#71717A]">For {value} · {data.range.label}</div>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'#FFC53D'}} />Sessions</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'#6EE7B7'}} />Revenue ({currency})</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={trendForChart} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="drillSess" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FFC53D" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#FFC53D" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="drillRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6EE7B7" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#6EE7B7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#2A2A2A" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#71717A" }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#71717A" }} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid #3A3A3A", fontSize: 11 }}
                    labelStyle={{ color: "#FFEB00" }}
                    formatter={(val, key) => key === "revenue" ? fmtCurrency(val, currency) : fmtNum(val, { decimals: 0 })}
                  />
                  <Area yAxisId="left" type="monotone" dataKey="sessions" stroke="#FFC53D" strokeWidth={1.5} fill="url(#drillSess)" />
                  <Area yAxisId="right" type="monotone" dataKey="revenue" stroke="#6EE7B7" strokeWidth={1.5} fill="url(#drillRev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* STEP CONTEXT BREAKDOWN */}
          {dimension === "step" && (data.context?.by_device || data.context?.by_channel) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="funnel-drilldown-step-context">
              <div className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="text-[11px] font-semibold text-white mb-2">By device</div>
                {Object.entries(data.context.by_device || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-[11px] py-1 border-b border-[#2A2A2A] last:border-b-0">
                    <span className="capitalize text-[#A1A1AA]">{k}</span>
                    <span className="font-mono text-white">{fmtNum(v.users, { compact: true, decimals: 0 })} users</span>
                    <span className="font-mono text-[10px]" style={{color: (v.drop_off_pct_from_prev||0) > 50 ? "#FF8080" : "#FCD34D"}}>
                      {v.drop_off_pct_from_prev != null ? `-${v.drop_off_pct_from_prev}%` : ""}
                    </span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="text-[11px] font-semibold text-white mb-2">By channel</div>
                {Object.entries(data.context.by_channel || {}).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-[11px] py-1 border-b border-[#2A2A2A] last:border-b-0">
                    <span className="text-[#A1A1AA]">{k}</span>
                    <span className="font-mono text-white">{fmtNum(v.users, { compact: true, decimals: 0 })} users</span>
                    <span className="font-mono text-[10px]" style={{color: (v.drop_off_pct_from_prev||0) > 50 ? "#FF8080" : "#FCD34D"}}>
                      {v.drop_off_pct_from_prev != null ? `-${v.drop_off_pct_from_prev}%` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI EXEC SUMMARY */}
          {data.ai?.summary && (
            <div className="p-3 rounded border" style={{ borderColor: "#FFEB0044", background: "#1A1A0F" }} data-testid="funnel-drilldown-ai-summary">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider text-[#FFEB00]">AI · Executive summary</span>
                {data.ai.confidence && <Pill bg="#52525B22" color="#A1A1AA">{data.ai.confidence} confidence</Pill>}
              </div>
              <p className="text-[12px] text-[#E4E4E7] leading-relaxed">{data.ai.summary}</p>
            </div>
          )}

          {/* RECOMMENDATIONS */}
          {Array.isArray(data.ai?.recommendations) && data.ai.recommendations.length > 0 && (
            <div data-testid="funnel-drilldown-recommendations">
              <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-2">Recommendations</div>
              <div className="space-y-2">
                {data.ai.recommendations.map((r, i) => {
                  const pc = priorityColor(r.priority);
                  return (
                    <div key={i} className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`funnel-drilldown-rec-${i}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {r.priority && <Pill bg={pc.bg} color={pc.color}>{r.priority}</Pill>}
                        {r.effort && <Pill>{r.effort} effort</Pill>}
                        <span className="text-[12px] font-semibold text-white">{r.title}</span>
                      </div>
                      <p className="text-[11px] text-[#A1A1AA] leading-relaxed">{r.detail}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
